import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {toLocalDateStr} from "../../../utils/dateHelpers";
import DeltaBadge from "./DeltaBadge";
import RevenueDetailsModal from "./RevenueDetailsModal";

const StatsCards = ({isMobile = false, _dateRange, _startTime, _endTime, isDarkMode = false, refreshKey = 0, comparisonRange = null}) => {
    const {t} = useTranslation("activite");
    const [stats, setNumbers] = useState({
        revenue_with_tax: 0,
        revenue_without_tax: 0,
        average_order_value: 0,
        sales_count: 0,
        sales_index: 0
    });
    // Metrics for the comparison period (null when comparison is off).
    const [previousStats, setPreviousStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showRevenueDetails, setShowRevenueDetails] = useState(false);

    // Format numbers with commas and 2 decimal places
    const formatCurrency = (value) => {
        // Make sure value is a number
        const numberValue = Number(value || 0);
        return numberValue.toLocaleString('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) + " DH";
    };

    useEffect(() => {
        const fetchMetrics = async () => {
            if (!_dateRange || !_dateRange[0] || !_dateRange[0].startDate || !_dateRange[0].endDate) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                if (!accountId || !accountId.id) {
                    setIsLoading(false);
                    return;
                }

                const startTimeFormatted = _startTime || "00:00";
                const endTimeFormatted   = _endTime || "23:59";

                const fetchRange = (range) => api.get(
                    `/activite/metrics?startDate=${toLocalDateStr(range.startDate)}&endDate=${toLocalDateStr(range.endDate)}&startTime=${startTimeFormatted}&endTime=${endTimeFormatted}`,
                    {
                        headers: {
                            "X-account-id": accountId.id,
                            "Accept": "application/json",
                            "Content-Type": "application/json"
                        }
                    }
                );

                const [response, previousResponse] = await Promise.all([
                    fetchRange(_dateRange[0]),
                    comparisonRange?.[0] ? fetchRange(comparisonRange[0]) : Promise.resolve(null),
                ]);

                if (response.data) {
                    setNumbers(response.data);
                }
                setPreviousStats(previousResponse?.data || null);
            } catch (error) {
                console.error("Error fetching metrics:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMetrics();
    }, [_dateRange, _startTime, _endTime, refreshKey, comparisonRange]);

    const formatIndex = (value) => value || 0;
    const statsData = [
        {label: t("statsCards.revenueTtc"), field: "revenue_with_tax", format: formatCurrency, onClick: () => setShowRevenueDetails(true)},
        {label: t("statsCards.revenueHt"), field: "revenue_without_tax", format: formatCurrency},
        {label: t("statsCards.averageOrder"), field: "average_order_value", format: formatCurrency},
        {label: t("statsCards.salesIndex"), field: "sales_index", format: formatIndex},
    ].map(stat => ({...stat, value: stat.format(stats[stat.field])}));

    // Carte cliquable (CA TTC → modal de détail) : bouton accessible au clavier.
    const clickableProps = (stat) => stat.onClick ? {
        role: "button",
        tabIndex: 0,
        title: t("statsCards.revenueDetails.open"),
        onClick: stat.onClick,
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); stat.onClick(); } },
    } : {};
    const clickableClass = (stat) => stat.onClick
        ? ` cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`
        : '';

    const renderDelta = (stat) => previousStats && (
        <DeltaBadge
            current={stats[stat.field]}
            previous={previousStats[stat.field] ?? 0}
            formatValue={stat.format}
            isDarkMode={isDarkMode}
        />
    );

    return (
        <>
            {isMobile ? (
                // Mobile : grille pleine largeur à filets (gap-px sur fond gris), comme le
                // résumé du Stock — pas de cartes arrondies ni de marge.
                <div className={`grid grid-cols-2 gap-px mt-4 md:hidden w-full border-y ${isDarkMode ? 'bg-gray-700 border-gray-700' : 'bg-gray-200 border-gray-200'}`}>
                    {isLoading ? (
                        Array(4).fill(0).map((_, index) => (
                            <div
                                key={index}
                                className={`p-4 min-h-[90px] flex flex-col justify-center items-center text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                            >
                                <div className="animate-pulse h-5 w-16 bg-gray-200 rounded mb-2"></div>
                                <div className="animate-pulse h-3 w-20 bg-gray-200 rounded"></div>
                            </div>
                        ))
                    ) : statsData.map((stat, index) => (
                        <div
                            key={index}
                            {...clickableProps(stat)}
                            className={`p-4 min-h-[90px] flex flex-col justify-center items-center text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}${clickableClass(stat)}`}
                        >
                            <p className={`text-sm font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{stat.value}</p>
                            <h3 className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</h3>
                            {renderDelta(stat)}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="hidden md:flex w-full md:w-1/4 mt-4 md:mt-0 items-center justify-center">
                    <div className="flex flex-col gap-4 w-full items-center justify-center">
                        {isLoading ? (
                            Array(4).fill(0).map((_, index) => (
                                <div
                                    key={index}
                                    className={`min-h-[100px] w-full flex flex-col justify-center items-center py-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                                >
                                    <div className="animate-pulse h-6 w-24 bg-gray-200 rounded mb-2"></div>
                                    <div className="animate-pulse h-4 w-32 bg-gray-200 rounded"></div>
                                </div>
                            ))
                        ) : statsData.map((stat, index) => (
                            <div
                                key={index}
                                {...clickableProps(stat)}
                                className={`min-h-[100px] w-full flex flex-col justify-center items-center py-4 transition-shadow duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}${clickableClass(stat)}`}
                            >
                                <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{stat.value}</p>
                                <h3 className={`text-sm mt-2 text-center uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</h3>
                                {renderDelta(stat)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {showRevenueDetails && (
                <RevenueDetailsModal
                    stats={stats}
                    formatCurrency={formatCurrency}
                    onClose={() => setShowRevenueDetails(false)}
                    isDarkMode={isDarkMode}
                />
            )}
        </>
    );
};

export default StatsCards;
